import { useLayoutEffect } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

import HideIfWebView from '#client/components/HideIfWebView';

import OboBanner from '../OboBanner';
import AppFooter from './AppFooter';
import AppHeader from './AppHeader';
import BusinessEntityNav from './BusinessEntityNav';
import { BusinessEntitySelector } from './BusinessEntitySelector';

import { useDrawerState } from './hooks/useDrawerState';

const Site = styled(Box)(({ theme }) => ({
  '--nav-width': '5.75rem',
  '--nav-width-wide': '17.5rem',

  background: theme.palette.focus.main,
  position: 'relative',
  transition: theme.transitions.create(['transform'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),

  'body.drawer-open &': {
    transform: 'translateX(var(--nav-width-wide))'
  },

  [theme.breakpoints.up('md')]: {
    display: 'grid',
    gridTemplateColumns: 'var(--nav-width) 1fr',
    gridTemplateAreas: '"nav content"',

    'body.drawer-open &': {
      transform: 'none'
    }
  }
}));

const Navigation = styled(Box)(({ theme }) => ({
  gridArea: 'nav',
  height: '100dvh',
  overflow: 'hidden',
  position: 'fixed',
  top: 0,
  bottom: 0,
  left: 'calc(-1 * var(--nav-width-wide))', // creates a negative CSS var value.
  width: 'var(--nav-width-wide)',
  zIndex: theme.zIndex.drawer,

  [theme.breakpoints.up('md')]: {
    position: 'sticky',
    top: 0,
    transition: 'width ease-in-out 0.3s',
    width: 'var(--nav-width)',

    '&:hover, &:focus-within': {
      width: 'var(--nav-width-wide)'
    }
  }
}));

const Content = styled(Box)(({ theme }) => ({
  gridArea: 'content',
  // Push footer to bottom of viewport on mobile when there is little to no content (e.g. loading).
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100dvh',

  [theme.breakpoints.up('md')]: {
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    gridTemplateColumns: 'minmax(0, 1fr) auto'
  }
}));

const Header = styled(Box, {
  shouldForwardProp: (prop) => !['isObo'].includes(prop)
})(({ isObo, theme }) => ({
  background: theme.palette.focus00.main,
  paddingInline: '1rem',
  paddingTop: isObo ? '1rem' : 'unset',
  zIndex: theme.zIndex.appBar,
  [theme.breakpoints.up('md')]: {
    gridRow: '1 / 2',
    gridColumn: '2 / 3',
    position: 'sticky',
    top: '0'
  }
}));

const Selector = styled(Box, {
  shouldForwardProp: (prop) => !['isObo'].includes(prop)
})(({ isObo, theme }) => ({
  background: theme.palette.focus00.main,
  padding: isObo ? '2rem' : '1rem',
  position: 'sticky',
  top: '0',
  zIndex: theme.zIndex.appBar,

  [theme.breakpoints.up('md')]: {
    gridRow: '1 / 2',
    gridColumn: '1 / 2'
  }
}));

const Main = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  paddingInline: '1rem',

  [theme.breakpoints.up('md')]: {
    flexBasis: '100%',
    paddingInline: '5%',
    gridColumn: '1 / 3'
  }
}));

const Footer = styled(Box)(({ theme }) => ({
  paddingInline: '1rem',

  [theme.breakpoints.up('md')]: {
    gridColumn: '1 / 3',
    paddingInline: '5%'
  }
}));

function AppContainer({ children }) {
  const { drawerState: isDrawerOpen } = useDrawerState();
  const { obo } = useAuthentication();
  const isObo = !!obo;

  useLayoutEffect(() => {
    const drawerOpenClass = 'drawer-open';

    if (isDrawerOpen) {
      document.body.classList.add(drawerOpenClass);
    } else {
      document.body.classList.remove(drawerOpenClass);
    }
  }, [isDrawerOpen]);

  return (
    <Site>
      <OboBanner obo={obo} />
      <Navigation component="section" className="site-navigation">
        <BusinessEntityNav />
      </Navigation>

      <Content>
        <HideIfWebView>
          <Header isObo={isObo} component="header">
            <AppHeader />
          </Header>

          <Selector isObo={isObo}>
            <BusinessEntitySelector />
          </Selector>
        </HideIfWebView>

        <Main component="main">
          {children}
        </Main>

        <Footer component="footer">
          <AppFooter />
        </Footer>
      </Content>
    </Site>
  );
}

AppContainer.displayName = 'AppContainer';
AppContainer.propTypes = {
  children: PropTypes.node
};

export default AppContainer;
